/**
 * /talent/signup — focused conversion page. Slim passport form (essentials
 * only) + benefits sidebar. On success we bounce to /thank-you-talent so
 * conversion pixels (Meta / Google Ads) fire with the right event.
 *
 * Keeps only the fields that materially help matching: name, email, phone,
 * desired role, city, years, resume PDF, work auth, availability. Everything
 * else lives in the full /talent flow.
 */
import React, { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
    CheckCircle2, Upload, Loader2, X, FileText, IdCard, Sparkles, ShieldCheck, Zap,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import { SocialProof } from "@/components/marketing/SocialProof";

const BENEFITS = [
    { icon: IdCard, title: "One profile, unlimited applications", body: "Build your Talent Passport once. Every Simonara employer sees you — no more copy/pasting the same resume." },
    { icon: ShieldCheck, title: "Verifications done for you", body: "Identity, email, phone, employment, references. All handled — arriving to interviews already trusted." },
    { icon: Zap, title: "AI matches you, not the other way around", body: "Roles find you. When a job's a fit, employers reach out on your terms." },
    { icon: Sparkles, title: "Career tools included", body: "AI resume review, interview prep, cover-letter drafts, salary coaching — free with your passport." },
];

export default function TalentSignup() {
    const navigate = useNavigate();
    const fileRef = useRef(null);
    const [resumeFile, setResumeFile] = useState(null);
    const [yearsExp, setYearsExp] = useState("");
    const [workAuth, setWorkAuth] = useState("");
    const [availability, setAvailability] = useState("");
    const [city, setCity] = useState("");
    const [desiredPosition, setDesiredPosition] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // iter-172 — Signup completeness meter (Section 6).
    // Six items drawn from the spec's exact list. Signup only directly
    // captures four of them; profile-photo and 3+ skills are flagged as
    // "add after signup" so the talent knows what's next. The meter is
    // ADVISORY only — incomplete signups still submit, and employers
    // never see or filter on this completeness number.
    const completenessItems = [
        { key: "experience",   label: "Experience",     done: !!yearsExp },
        { key: "location",     label: "Location",       done: !!city.trim() },
        { key: "work_prefs",   label: "Work preferences", done: !!desiredPosition.trim() && !!workAuth },
        { key: "availability", label: "Availability",   done: !!availability },
        { key: "photo",        label: "Profile photo",  done: false, post: true },
        { key: "skills",       label: "3+ skills",      done: false, post: true },
    ];
    const doneCount = completenessItems.filter((x) => x.done).length;
    const completenessPct = Math.round((doneCount / completenessItems.length) * 100);
    const nextMissing = completenessItems.find((x) => !x.done);

    const onFile = (f) => {
        if (!f) return;
        if (!f.name.toLowerCase().endsWith(".pdf")) {
            toast.error("Please upload a PDF resume.");
            return;
        }
        if (f.size > 8 * 1024 * 1024) {
            toast.error("File too large. Max 8 MB.");
            return;
        }
        setResumeFile(f);
    };

    const submit = async (e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        if (!resumeFile) {
            toast.error("Please upload your resume (PDF) before submitting.");
            return;
        }
        const out = new FormData();
        out.append("name", fd.get("name") || "");
        out.append("email", fd.get("email") || "");
        out.append("phone", fd.get("phone") || "");
        out.append("city", fd.get("city") || "");
        out.append("desired_position", fd.get("desired_position") || "");
        out.append("years_experience", yearsExp);
        out.append("linkedin_url", fd.get("linkedin_url") || "");
        out.append("work_authorization", workAuth);
        out.append("availability", availability);
        out.append("resume", resumeFile);
        if (!out.get("name") || !out.get("email")) {
            toast.error("Name and email are required.");
            return;
        }
        try {
            setSubmitting(true);
            await api.post("/resume-upload", out, { headers: { "Content-Type": "multipart/form-data" } });
            // Stash email so /thank-you-talent can build the "finish setup" CTA
            // without asking again.
            try { sessionStorage.setItem("simonara_signup_email", out.get("email")); } catch { /* quota */ }
            navigate("/thank-you-talent");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-background" data-testid="talent-signup-page">
            <Helmet>
                <title>Build Your Profile · Simonara Talent Solutions</title>
                <meta name="description" content="Create your verified Talent Passport in 2 minutes. Get discovered by employers actively hiring on Simonara." />
                <link rel="canonical" href="/talent/signup" />
            </Helmet>

            <section className="container-page pt-14 pb-8">
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="max-w-2xl"
                >
                    <p className="overline">Get started · Talent</p>
                    <h1 className="mt-2 font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-foreground">
                        Build your Talent Passport in two minutes.
                    </h1>
                    <p className="mt-3 text-muted-foreground">
                        Upload your resume, tell us what you're open to, and we'll match you to roles
                        as employers post them. No more chasing applications.
                    </p>
                </motion.div>
            </section>

            <section className="container-page pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        {/* iter-172 — Completeness meter (Section 6).
                            Advisory only — never blocks submit, never
                            down-ranks incomplete profiles. */}
                        <div
                            className="card-soft p-5 mb-5"
                            data-testid="talent-signup-completeness"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Talent Passport</div>
                                    <div className="mt-1 font-heading font-semibold text-foreground text-lg" data-testid="talent-signup-completeness-pct">
                                        Profile {completenessPct}% complete
                                    </div>
                                </div>
                                {nextMissing && (
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Next</div>
                                        <div className="text-sm font-medium text-foreground" data-testid="talent-signup-completeness-next">
                                            {nextMissing.label}
                                            {nextMissing.post && <span className="text-muted-foreground text-xs"> · after signup</span>}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                                <div
                                    className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                                    style={{ width: `${completenessPct}%` }}
                                    data-testid="talent-signup-completeness-bar"
                                />
                            </div>
                            <p className="mt-2 text-xs text-muted-foreground" data-testid="talent-signup-completeness-prompt">
                                Complete your passport to appear in more employer searches.
                            </p>
                        </div>
                        <form
                            onSubmit={submit}
                            className="card-soft p-7 grid grid-cols-1 sm:grid-cols-2 gap-4"
                            data-testid="talent-signup-form"
                            data-conversion="talent_signup_form"
                        >
                            <div className="sm:col-span-2">
                                <Label htmlFor="ts-name">Full name *</Label>
                                <Input id="ts-name" name="name" required className="mt-1.5" data-testid="ts-name" />
                            </div>
                            <div>
                                <Label htmlFor="ts-email">Email *</Label>
                                <Input id="ts-email" name="email" type="email" required className="mt-1.5" data-testid="ts-email" />
                            </div>
                            <div>
                                <Label htmlFor="ts-phone">Phone</Label>
                                <Input id="ts-phone" name="phone" className="mt-1.5" data-testid="ts-phone" />
                            </div>
                            <div>
                                <Label htmlFor="ts-city">City</Label>
                                <Input id="ts-city" name="city" placeholder="e.g. Austin, TX" className="mt-1.5" data-testid="ts-city" value={city} onChange={(e) => setCity(e.target.value)} />
                            </div>
                            <div>
                                <Label htmlFor="ts-role">Desired role</Label>
                                <Input id="ts-role" name="desired_position" placeholder="e.g. Senior Product Designer" className="mt-1.5" data-testid="ts-role" value={desiredPosition} onChange={(e) => setDesiredPosition(e.target.value)} />
                            </div>
                            <div>
                                <Label>Years of experience</Label>
                                <Select value={yearsExp} onValueChange={setYearsExp}>
                                    <SelectTrigger className="mt-1.5" data-testid="ts-years"><SelectValue placeholder="Select range" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="0-1">0 – 1 years</SelectItem>
                                        <SelectItem value="2-4">2 – 4 years</SelectItem>
                                        <SelectItem value="5-7">5 – 7 years</SelectItem>
                                        <SelectItem value="8-10">8 – 10 years</SelectItem>
                                        <SelectItem value="10+">10+ years</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Work authorization</Label>
                                <Select value={workAuth} onValueChange={setWorkAuth}>
                                    <SelectTrigger className="mt-1.5" data-testid="ts-work-auth"><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="US Citizen">US Citizen</SelectItem>
                                        <SelectItem value="Green Card">Green Card</SelectItem>
                                        <SelectItem value="H1-B">H1-B</SelectItem>
                                        <SelectItem value="Other Work Visa">Other Work Visa</SelectItem>
                                        <SelectItem value="Requires Sponsorship">Requires Sponsorship</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Availability</Label>
                                <Select value={availability} onValueChange={setAvailability}>
                                    <SelectTrigger className="mt-1.5" data-testid="ts-availability"><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Immediately">Immediately</SelectItem>
                                        <SelectItem value="2 weeks">Within 2 weeks</SelectItem>
                                        <SelectItem value="1 month">Within a month</SelectItem>
                                        <SelectItem value="Passive">Just exploring</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="sm:col-span-2">
                                <Label htmlFor="ts-linkedin">LinkedIn profile (optional)</Label>
                                <Input id="ts-linkedin" name="linkedin_url" type="url" placeholder="https://linkedin.com/in/..." className="mt-1.5" data-testid="ts-linkedin" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Resume (PDF, max 8 MB) *</Label>
                                {resumeFile ? (
                                    <div className="mt-1.5 flex items-center justify-between gap-3 p-3 rounded-md border border-emerald-500/40 bg-emerald-500/5" data-testid="ts-resume-selected">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <FileText size={16} className="text-emerald-600 shrink-0" />
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium truncate">{resumeFile.name}</div>
                                                <div className="text-[11px] text-muted-foreground">{(resumeFile.size / 1024).toFixed(0)} KB</div>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setResumeFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                                            className="text-muted-foreground hover:text-foreground p-1 rounded"
                                            data-testid="ts-resume-clear"
                                            aria-label="Remove selected resume"
                                        ><X size={14} /></button>
                                    </div>
                                ) : (
                                    <label
                                        htmlFor="ts-resume-input"
                                        className="mt-1.5 flex items-center gap-2 px-3 py-3 rounded-md border border-dashed border-border bg-background hover:border-emerald-500/60 hover:bg-emerald-500/5 cursor-pointer transition"
                                        data-testid="ts-resume-dropzone"
                                    >
                                        <Upload size={16} className="text-muted-foreground" />
                                        <span className="text-sm text-muted-foreground">
                                            <span className="font-medium text-foreground">Click to upload</span> your resume PDF.
                                        </span>
                                    </label>
                                )}
                                <input
                                    ref={fileRef}
                                    id="ts-resume-input"
                                    type="file"
                                    accept=".pdf,application/pdf"
                                    onChange={(e) => onFile(e.target.files?.[0])}
                                    className="hidden"
                                    data-testid="ts-resume-input"
                                />
                            </div>
                            <div className="sm:col-span-2 mt-2">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-green w-full disabled:opacity-60 inline-flex items-center justify-center gap-2"
                                    data-testid="ts-submit"
                                >
                                    {submitting && <Loader2 size={14} className="animate-spin" />}
                                    Build my Talent Passport
                                </button>
                                <p className="mt-2 text-xs text-muted-foreground text-center">
                                    By submitting you agree to our{" "}
                                    <Link to="/terms" className="underline">Terms</Link> and{" "}
                                    <Link to="/privacy" className="underline">Privacy Policy</Link>.
                                </p>
                            </div>
                        </form>
                    </div>
                    <aside className="lg:col-span-5 order-1 lg:order-2">
                        <div className="card-soft p-6 sticky top-24">
                            <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-600 dark:text-emerald-400">
                                What you unlock
                            </div>
                            <ul className="mt-4 space-y-4">
                                {BENEFITS.map((b) => (
                                    <li key={b.title} className="flex gap-3">
                                        <div className="mt-0.5 w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                                            <b.icon size={16} />
                                        </div>
                                        <div>
                                            <div className="text-sm font-semibold text-foreground">{b.title}</div>
                                            <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{b.body}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-5 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-foreground inline-flex items-start gap-2">
                                <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                                <span>100% free for professionals. Your data is never sold. Cancel your passport at any time.</span>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>

            <SocialProof compact />
        </div>
    );
}
